/**
 * Adding a sport, and editing one — the same screen either way.
 *
 * They were two screens and six steps between them. A sport is not a sequence:
 * a kind of fixture names a master, a config names a kind, and answering those
 * in separate rooms meant never seeing whether they agreed.
 */

import { useSearchParams } from "react-router-dom";

import { Crumbs, PageHead, Problem } from "@/components/kit";
import { SportWizard } from "@/components/sport/SportWizard";
import { getDefinition, getDraft, listDefinitions } from "@/lib/api/client";
import type { SportDefinitionDoc } from "@/lib/api/types";
import { emptyDefinition } from "@/lib/builder";
import { displayName } from "@/lib/format";
import { useApi } from "@/spa/useApi";

export function NewSportPage() {
  const [search] = useSearchParams();
  const from = search.get("from") ?? undefined;

  const { data, loading, error } = useApi(
    async () => {
      const codes = [...new Set((await listDefinitions()).map((d) => d.sportCode))];
      // Editing reopens the open draft if there is one, else the live version.
      let initial: SportDefinitionDoc | undefined;
      if (from) {
        initial = await getDraft(from).catch(() => getDefinition(from).catch(() => undefined));
      }
      return { codes, initial };
    },
    [from],
    "failed to load",
  );

  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;
  if (!data) return <PageHead title={from ?? "New sport"} note={loading ? "Loading…" : ""} />;

  return (
    <>
      <Crumbs trail={[{ label: "Sports", href: "/sports" }, { label: from ?? "New" }]} />
      <SportWizard
        initial={data.initial ?? emptyDefinition()}
        sports={data.codes.map((code) => ({ code, label: displayName(code) }))}
      />
    </>
  );
}
